import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useMutation } from '@tanstack/react-query';

import { getPublicAssetPath } from '../../../shared/config/publicAssetPath';
import { getCookie, setAccessToken, setRefreshToken } from '../../../shared/cookie';
import { AxiosCommonError } from '../../../shared/types';
import { handleError } from '../../ui/err/handleError';

import type { AxiosError } from 'axios';
import { authApis } from '../../../shared/apis/auth/apis';

const SAVE_ID_KEY = 'admin_saveId';

const Login = () => {
  // useNavigate 선언
  const navigate = useNavigate();

  // react-hook-form 타입정의
  interface FormProps {
    email: string;
    password: string;
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormProps>({ mode: 'onChange' });

  const { mutate: mutateLogin } = useMutation(authApis.login, {
    onSuccess: (res) => {
      setAccessToken(res.data.accessToken);
      setRefreshToken(res.data.refreshToken);
      Toast.fire({
        icon: 'success',
        title: '<span style="font-size: 14px">관리자 권한으로 로그인 요청 대기중입니다.',
        width: 330,
      }).then(() => {
        window.location.reload();
      });
    },
    onError: (e: AxiosError<AxiosCommonError>) => {
      handleError(e);
    },
  });

  // 폼 버튼 클릭시 작동하는 함수
  const onSubmit = async (data: FormProps) => {
    // 아이디 저장 여부 확인
    save_id
      ? (document.cookie = `${SAVE_ID_KEY}=${data.email}; max-age=604800; path=/`)
      : (document.cookie = `${SAVE_ID_KEY}=${data.email}; path=/`);

    mutateLogin(data);
  };

  // sweet-alert 모달창
  const Toast = Swal.mixin({
    toast: true,
    position: 'center',
    showConfirmButton: false,
    timer: 1000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener('mouseenter', Swal.stopTimer);
      toast.addEventListener('mouseleave', Swal.resumeTimer);
    },
  });

  // 캡스락 활성 여부 확인
  const [capslock, setCapsLock] = useState<boolean>(false);

  // 아이디 저장
  const [save_id, SetSaveId] = useState<boolean>(false);

  // 아이디 저장 체크박스 여부 검증
  useEffect(() => {
    if (getCookie(SAVE_ID_KEY)) {
      SetSaveId(true);
    }
  }, []);

  // password 정규식
  const passwordRegEx = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,16}$/;

  return (
    <Wrap>
      <Container>
        <img
          src={getPublicAssetPath('/images/main_logo.png')}
          alt="엣지크로스 로고"
          onClick={() => {
            window.location.reload();
          }}
        />
        <PostForm onSubmit={handleSubmit(onSubmit)}>
          <Line>
            <label htmlFor="email">이메일</label>
            <Input
              id="email"
              type="email"
              autoComplete="off"
              placeholder="이메일을 입력해주세요"
              isInvalid={!!errors.email}
              defaultValue={getCookie(SAVE_ID_KEY)}
              {...register('email', {
                required: '이메일을 입력해주세요',
              })}
            />
            {errors.email && <div className="err">{errors.email.message}</div>}
          </Line>
          <Line className="pw-line">
            <label htmlFor="password" style={{ marginBottom: '2px' }}>
              비밀번호
            </label>
            <p style={{ color: 'blue', marginBottom: '10px', fontSize: '14px' }}>
              MFM에 가입한 회원은 MFM 비밀번호를 입력해 주세요.
            </p>
            <Input
              id="password"
              type="password"
              autoComplete="off"
              placeholder="영문/숫자/특수문자 포함 8~16자"
              isInvalid={!!errors.password}
              onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                e.getModifierState('CapsLock') === true ? setCapsLock(true) : setCapsLock(false);
              }}
              {...register('password', {
                required: '영문,숫자,특수문자(!@#$%^&*)를 1개 이상 조합하여 입력해주세요',
                pattern: {
                  value: passwordRegEx,
                  message: '비밀번호 형식에 맞지 않습니다.',
                },
              })}
            />
            {errors.password && (
              <>
                <div className="err">{errors.password.message}</div>
                <div className="err">MFM 가입 이력이 있는 경우, MFM 비밀번호를 입력해주세요.</div>
              </>
            )}

            {capslock && <div className="err capslock">CapsLock이 켜져 있습니다.</div>}
          </Line>
          <div className="tool-box">
            <div className="left-box">
              <input
                id="save"
                type="checkbox"
                checked={save_id}
                onClick={() => SetSaveId(!save_id)}
                style={{ cursor: 'pointer' }}
                readOnly
              />
              <label htmlFor="save" style={{ cursor: 'pointer' }}>
                아이디 저장
              </label>
            </div>
          </div>
          <Button>
            <button className="signUp-email">로그인</button>
          </Button>
        </PostForm>
        <div className="signUp-redirection">
          추가 계정이 필요하신가요?{' '}
          <span
            onClick={() => {
              navigate('/signup');
            }}
          >
            회원가입하기
          </span>
        </div>
      </Container>
    </Wrap>
  );
};

export default Login;
const Wrap = styled.main`
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-size: 1rem;
`;
const Container = styled.main`
  width: 420px;
  min-height: 660px;
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  .tool-box {
    display: flex;
    .left-box {
      cursor: pointer;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 5px;
    }
    input {
      width: 15px;
      height: 15px;
      color: ${(props) => props.theme.color.blue};
      vertical-align: middle;
      -webkit-appearance: none;
      background: none;
      border: 0;
      outline: 0;
      flex-grow: 0;
      border-radius: 50%;
      background-color: ${(props) => props.theme.color.white};
      transition: background-color 300ms;
      cursor: pointer;
      &::before {
        content: '';
        color: transparent;
        display: block;
        width: inherit;
        height: inherit;
        border-radius: inherit;
        border: 0;
        background-color: transparent;
        background-size: contain;
        box-shadow: inset 0 0 0 1px ${(props) => props.theme.color.shadow.gray};
      }
      &:checked {
        background-color: currentcolor;
      }
      &:checked::before {
        box-shadow: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E %3Cpath d='M15.88 8.29L10 14.17l-1.88-1.88a.996.996 0 1 0-1.41 1.41l2.59 2.59c.39.39 1.02.39 1.41 0L17.3 9.7a.996.996 0 0 0 0-1.41c-.39-.39-1.03-.39-1.42 0z' fill='%23fff'/%3E %3C/svg%3E");
      }
    }
  }
  img {
    cursor: pointer;
    width: 320px;
    margin-bottom: 10px;
  }
  .signUp-redirection {
    width: 100%;
    margin-top: 10px;
    span {
      font-size: 15px;
      text-decoration: underline;
      cursor: pointer;
      color: ${(props) => props.theme.color.purple};
    }
  }
`;
const PostForm = styled.form`
  width: 100%;
`;
const Line = styled.div`
  display: flex;
  flex-direction: column;
  margin: 10px 0;
  .err {
    color: ${(props) => props.theme.color.error};
    font-size: 12px;
    margin-top: 5px;
  }
  .err.capslock {
    color: ${(props) => props.theme.color.blue};
  }
  label {
    margin-bottom: 10px;
  }
`;
const Input = styled.input`
  border: ${(props) => props.theme.border.gray1};
  width: 100%;
  border-radius: 3px;
  height: 50px;
  padding: 10px;
  font-size: 14px;
  outline: ${(props: { isInvalid: boolean }) => props.isInvalid && 'none'};
  border: ${(props: { isInvalid: boolean }) => props.isInvalid && '1px solid red'};
  border-color: ${(props: { isInvalid: boolean }) => props.isInvalid && '#fa5963'};
  &:focus {
    border: ${(props) => props.theme.border.blue2};
    box-shadow: 0 0 0 2px ${(props) => props.theme.color.shadow.blue};
    outline: none;
  }
  &:focus::placeholder {
    color: transparent;
  }
`;
const Button = styled.div`
  margin-top: 20px;
  button {
    width: 100%;
    height: 50px;
    font-size: 15px;
  }
  .signUp-email {
    background-color: ${(props) => props.theme.color.red};
  }
`;
